import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  TagEditForm,
  TagEditInitialValues,
  TagComponents,
  TagAdditionalParams,
  TagFormOptions,
  onTagFormSaved,
  ITagFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedTag, ISharedPatchTag, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchTag, patchTag, getTagUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedTag> {}

const TagFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchTag(id).then(tag => setValue(tag));
  }, [id, setValue, ]);

  const param_props: ITagFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && TagEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && TagFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchTag, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchTag) => value && save(() => patchTag(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onTagFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Tag, value)}
    >
      <AtlasForm<ISharedPatchTag>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={TagComponents}
        additionalParams={TagAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={TagEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('TAG')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const TagFormEditView = React.memo(withEdit(TagFormEdit, getTagUpdateSchema));
